// contexts/AuthContext.tsx
// Authentication context — manages user session and custom claims.

import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    User,
    IdTokenResult,
    signInWithEmailAndPassword,
    signOut as firebaseSignOut,
    onAuthStateChanged,
    sendPasswordResetEmail,
} from 'firebase/auth';
import { auth } from '../firebase/config';

export interface AdminClaims {
    accountId: string;
    accountType: string;            // 'parent' | 'teen' | 'admin'
    adminRoles?: string[];          // 'super_admin' | 'regional_admin' | 'content_moderator' | 'support_agent'
    adminScope?: { region?: string; features?: string[] };
    clients?: string[];             // e.g. ['admin_panel']
    consentVerified?: boolean;
}

interface AuthContextType {
    currentUser: User | null;
    claims: AdminClaims | null;
    loading: boolean;
    signIn: (email: string, password: string) => Promise<void>;
    signOut: () => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
    refreshClaims: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

const loadClaimsFromUser = async (user: User): Promise<AdminClaims | null> => {
    try {
        const result: IdTokenResult = await user.getIdTokenResult();
        return result.claims as unknown as AdminClaims;
    } catch {
        return null;
    }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<User | null>(null);
    const [claims, setClaims] = useState<AdminClaims | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setCurrentUser(user);
            if (user) {
                const c = await loadClaimsFromUser(user);
                setClaims(c);
            } else {
                setClaims(null);
            }
            setLoading(false);
        });
        return unsubscribe;
    }, []);

    const signIn = async (email: string, password: string) => {
        try {
            const credential = await signInWithEmailAndPassword(auth, email, password);
            // Force-refresh the ID token so we pick up any custom claims set by onAuthCreate.
            await credential.user.getIdToken(true);
            const c = await loadClaimsFromUser(credential.user);
            setClaims(c);
        } catch (error: any) {
            if (error.code === 'auth/invalid-credential') {
                throw new Error('Invalid email or password');
            } else if (error.code === 'auth/user-not-found') {
                throw new Error('No account found with this email');
            } else if (error.code === 'auth/wrong-password') {
                throw new Error('Incorrect password');
            } else if (error.code === 'auth/too-many-requests') {
                throw new Error('Too many failed attempts. Please try again later');
            } else {
                throw new Error('Failed to sign in. Please try again');
            }
        }
    };

    const signOut = async () => {
        await firebaseSignOut(auth);
        setClaims(null);
    };

    const resetPassword = async (email: string) => {
        await sendPasswordResetEmail(auth, email);
    };

    /**
     * Force a token refresh and reload claims. Call this after a super_admin
     * updates another admin's roles so the UI reflects the new state immediately.
     */
    const refreshClaims = async () => {
        if (currentUser) {
            await currentUser.getIdToken(true);
            const c = await loadClaimsFromUser(currentUser);
            setClaims(c);
        }
    };

    const value: AuthContextType = {
        currentUser,
        claims,
        loading,
        signIn,
        signOut,
        resetPassword,
        refreshClaims,
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
